import type {
  CreateOfferRequest,
  OfferDetailResponse,
  UpdateOfferRequest,
} from "@codway-dev/shared";
import { stripShopifyGid } from "@codway-dev/shared";
import type { OneTickTriggerType } from "../pages/sales-booster/one-tick-editor/OneTickEditorConfig";
import type {
  OneTickBorderStyle,
  OneTickSelectedProduct,
} from "../pages/sales-booster/one-tick-editor/OneTickEditorDesign";
import {
  buildTriggers,
  readTriggers,
  type TriggerProduct,
} from "./triggers.mapping";

// ─── Editor state shape ──────────────────────────────────────────────────────

export interface OneTickEditorState {
  internalName: string;
  isActive: boolean;

  triggerType: OneTickTriggerType;
  triggerProducts: TriggerProduct[];

  upsellTitle: string;
  upsellPrice: number;
  checkboxTemplate: string;
  description: string;

  textColor: string;
  descriptionColor: string;

  linkedProductEnabled: boolean;
  selectedProduct: OneTickSelectedProduct | null;

  imageUrl: string;

  backgroundColor: string;
  borderStyle: OneTickBorderStyle;
  borderWidth: number;
  borderColor: string;
  borderRadius: number;
}

export const DEFAULT_ONE_TICK_EDITOR_STATE: OneTickEditorState = {
  internalName: "Nuevo upsell",
  isActive: true,

  triggerType: "all",
  triggerProducts: [],

  upsellTitle: "Nombre Oferta",
  upsellPrice: 0,
  checkboxTemplate: "Agrega {title} por solo {price}",
  description: "",

  textColor: "rgba(0,0,0,1)",
  descriptionColor: "rgba(89,89,89,1)",

  linkedProductEnabled: false,
  selectedProduct: null,

  imageUrl: "",

  backgroundColor: "rgba(217,235,246,1)",
  borderStyle: "solid",
  borderWidth: 1,
  borderColor: "rgba(0,116,191,1)",
  borderRadius: 8,
};

// ─── Target ──────────────────────────────────────────────────────────────────

function buildTarget(state: OneTickEditorState) {
  if (state.linkedProductEnabled && state.selectedProduct) {
    // Linked product: `display_meta` carries the *product's* metadata
    // straight from the picker. Upsell-specific overrides live in
    // `display_data` (`product_title_override`, `image_url`,
    // `description`) and must NOT be written here, otherwise saving an
    // upsell silently overwrites the product's metadata copy and
    // conflates two semantically distinct fields.
    //
    // `shopify_variant_id` is the product's default (first) variant.
    // Required so the order pipeline can build a Shopify line — without
    // it the BE has no variant to apply the upsell pricing_logic against
    // and Shopify rejects the line with "price must be provided".
    return {
      type: "shopify_product" as const,
      shopify_product_id: state.selectedProduct.id,
      shopify_variant_id: state.selectedProduct.defaultVariantId,
      display_meta: {
        title: state.selectedProduct.title,
        image_url: state.selectedProduct.imageUrl || undefined,
        description: state.selectedProduct.description || undefined,
        price: state.selectedProduct.price || undefined,
      },
    };
  }
  // Synthetic upsell: no Shopify product attached. The upsell's own
  // fields land in `display_meta` as the only available carrier. The
  // `synthetic` type is explicit so downstream consumers can branch on
  // it instead of inferring "no IDs" from absence.
  return {
    type: "synthetic" as const,
    display_meta: {
      title: state.upsellTitle,
      image_url: state.imageUrl || undefined,
      description: state.description || undefined,
    },
  };
}

function readTarget(
  target: OfferDetailResponse["target"]
): OneTickSelectedProduct | null {
  const rawId =
    target.shopify_product_id ?? target.shopify_variant_id ?? target.bundle_id;
  if (!rawId) return null;
  // Defensive strip in case a legacy row carries the full GID.
  return {
    id: stripShopifyGid(rawId),
    title: target.display_meta?.title ?? "Producto",
    price: target.display_meta?.price ?? "0.00",
    imageUrl: target.display_meta?.image_url,
    description: target.display_meta?.description,
    defaultVariantId: target.shopify_variant_id
      ? stripShopifyGid(target.shopify_variant_id)
      : undefined,
  };
}

// ─── Display data ────────────────────────────────────────────────────────────

function buildDisplayData(state: OneTickEditorState) {
  return {
    component_type: "one_tick" as const,
    checkbox_text_template: state.checkboxTemplate,
    // Carries the merchant-edited upsell title, independent from the
    // linked product's own title (which lives in `target.display_meta`).
    // Only emitted for the linked-product flow; for synthetic upsells
    // the title is round-tripped through `target.display_meta.title`.
    product_title_override:
      state.linkedProductEnabled && state.upsellTitle
        ? state.upsellTitle
        : undefined,
    description: state.description || undefined,
    image_url: state.imageUrl || undefined,
    text_color: state.textColor || undefined,
    description_color: state.descriptionColor || undefined,
    styles: {
      background_color: state.backgroundColor || undefined,
      border_style: state.borderStyle,
      border_width: state.borderWidth,
      border_color: state.borderColor || undefined,
      border_radius: state.borderRadius,
    },
  };
}

// ─── Public API ──────────────────────────────────────────────────────────────

export function stateToCreateRequest(
  state: OneTickEditorState
): CreateOfferRequest {
  return {
    internal_name: state.internalName,
    is_active: state.isActive,
    priority: 0,
    config: {
      display_type: "one_tick",
      display_data: buildDisplayData(state),
      pricing_logic: {
        action: "fixed_price_per_item",
        amount: state.upsellPrice,
      },
    },
    target: buildTarget(state),
    triggers: buildTriggers(state.triggerType, state.triggerProducts),
  };
}

export function stateToUpdateRequest(
  state: OneTickEditorState,
  originalConfig?: OfferDetailResponse["config"] | null
): UpdateOfferRequest {
  const create = stateToCreateRequest(state);

  if (
    originalConfig?.display_type === "one_tick" &&
    create.config.display_type === "one_tick"
  ) {
    return {
      ...create,
      config: {
        ...(originalConfig as Record<string, unknown>),
        ...create.config,
        display_data: {
          ...(originalConfig.display_data as Record<string, unknown>),
          ...create.config.display_data,
        } as typeof create.config.display_data,
      },
    };
  }

  return create;
}

export function offerToState(offer: OfferDetailResponse): OneTickEditorState {
  const config = offer.config;

  if (config.display_type !== "one_tick") {
    throw new Error(
      `Este editor solo admite ofertas de tipo "one_tick". La oferta "${offer.internal_name}" es de tipo "${config.display_type}".`
    );
  }

  const dd = config.display_data;
  const styles = (dd.styles ?? {}) as Record<string, unknown>;

  const triggers = readTriggers(offer.triggers);
  const target = readTarget(offer.target);

  const pricing = config.pricing_logic;
  const upsellPrice =
    pricing.action === "fixed_price_per_item" ? pricing.amount : 0;
  // upsellTitle preference:
  //   1. `display_data.product_title_override` — canonical home for the
  //      merchant-edited title (linked-product flow).
  //   2. `target.display_meta.title` — backward compat with offers
  //      saved before the override field existed (the previous
  //      workaround conflated upsellTitle into display_meta.title), and
  //      the round-trip carrier for synthetic upsells.
  //   3. Empty string fallback.
  const upsellTitle =
    dd.product_title_override ?? offer.target.display_meta?.title ?? "";

  return {
    internalName: offer.internal_name,
    isActive: offer.is_active,

    triggerType: triggers.triggerType,
    triggerProducts: triggers.triggerProducts,

    upsellTitle,
    upsellPrice,
    checkboxTemplate:
      dd.checkbox_text_template ??
      DEFAULT_ONE_TICK_EDITOR_STATE.checkboxTemplate,
    description: dd.description ?? "",

    textColor: dd.text_color ?? DEFAULT_ONE_TICK_EDITOR_STATE.textColor,
    descriptionColor:
      dd.description_color ?? DEFAULT_ONE_TICK_EDITOR_STATE.descriptionColor,

    linkedProductEnabled: !!target,
    selectedProduct: target,

    imageUrl: dd.image_url ?? "",

    backgroundColor:
      (styles.background_color as string) ??
      DEFAULT_ONE_TICK_EDITOR_STATE.backgroundColor,
    borderStyle:
      (styles.border_style as OneTickBorderStyle) ??
      DEFAULT_ONE_TICK_EDITOR_STATE.borderStyle,
    borderWidth:
      (styles.border_width as number) ??
      DEFAULT_ONE_TICK_EDITOR_STATE.borderWidth,
    borderColor:
      (styles.border_color as string) ??
      DEFAULT_ONE_TICK_EDITOR_STATE.borderColor,
    borderRadius:
      (styles.border_radius as number) ??
      DEFAULT_ONE_TICK_EDITOR_STATE.borderRadius,
  };
}
